import { useState } from "react";
import { UserPlus, Copy, Check, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export function InviteMechanic({ shopName }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const link = `${window.location.origin}/register`;
  const shop = shopName ? ` (invited by ${shopName})` : "";
  const message = `Join me on Pitcrew${shop} — get local vehicle repair jobs from customers in your area, send quotes, and get paid. Sign up free: ${link}`;

  const copy = async (text, label) => {
    let ok = false;
    try { await navigator.clipboard.writeText(text); ok = true; }
    catch {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      document.body.removeChild(ta);
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(`${label} copied! Paste it into a text, email or group chat.`);
    } else {
      toast.message("Here's your invite link", { description: link });
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Join me on Pitcrew", text: message, url: link }); }
      catch { /* user dismissed */ }
    } else {
      copy(message, "Invite");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="invite-mechanic-button"
          className="bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800">
          <UserPlus className="h-4 w-4 mr-1.5" /> Invite a Mechanic
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100" data-testid="invite-mechanic-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><UserPlus className="h-5 w-5 text-amber-400" /> Invite a Mechanic</DialogTitle>
          <DialogDescription className="text-slate-400">
            Grow the network — invite other shops and mechanics to join Pitcrew. More pros means more competitive bids for local customers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Invite link</p>
            <div className="flex gap-2">
              <Input data-testid="invite-link-input" readOnly value={link}
                className="h-10 bg-slate-950 border-slate-700 text-slate-300 focus-visible:ring-amber-500/50" />
              <Button data-testid="copy-invite-link" onClick={() => copy(link, "Link")}
                className="h-10 shrink-0 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1.5">Ready-to-send message</p>
            <p className="text-sm text-slate-300 leading-relaxed" data-testid="invite-message">{message}</p>
          </div>

          <div className="flex gap-2">
            <Button data-testid="copy-invite-message" onClick={() => copy(message, "Invite")}
              variant="outline" className="flex-1 bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800">
              <Copy className="h-4 w-4 mr-1.5" /> Copy message
            </Button>
            <Button data-testid="share-invite" onClick={nativeShare}
              className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              <Share2 className="h-4 w-4 mr-1.5" /> Share
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
